import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';


interface Meeting {
  id: string;
  title: string;
  description: string;
  scheduledAt: string;
  date?: string;
  duration: number;
  location: string;
  meetingType: string;
  status: string;
}

interface ChamaMember {
  id: string;
  user_id: string;
  name?: string;
  user_name?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  user?: {
    name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
  role?: string;
}

interface AttendanceRecord {
  userId: string;
  userName?: string;
  isPresent: boolean;
  attendanceType: string;
  markedAt?: string;
}

interface MeetingDocument {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  uploadedAt: string;
}

const PhysicalMeetingAttendance: React.FC = () => {
  const { chamaId, meetingId } = useParams<{ chamaId: string; meetingId: string }>();
  const navigate = useNavigate();
  const colors = getThemeColors('dark');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [chamaMembers, setChamaMembers] = useState<ChamaMember[]>([]);
  const [attendance, setAttendance] = useState<{[userId: string]: boolean}>({});
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [notes, setNotes] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState<MeetingDocument[]>([]);
  const [previousMeeting, setPreviousMeeting] = useState<Meeting | null>(null);
  const [previousMinutes, setPreviousMinutes] = useState<{content: string; status: string} | null>(null);
  const [prevLoading, setPrevLoading] = useState(false);
  const [showPrevMinutes, setShowPrevMinutes] = useState(false);
  const [hasBackendSchemaIssue, setHasBackendSchemaIssue] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    if (meetingId && chamaId) {
      loadAllData();
    }
  }, [meetingId, chamaId]);

  const loadAllData = async () => {
    setLoading(true);
    setLoadingProgress(0);
    setConnectionError(null);

    try {
      // Load data in parallel for better performance
      const promises = [
        loadMeetingData(),
        loadChamaMembers(),
        loadExistingAttendance(),
        loadMeetingDocuments(),
        loadPreviousMeetingAndMinutes(),
      ];

      // Update progress as each promise completes
      let completed = 0;
      const updateProgress = () => {
        completed++;
        setLoadingProgress((completed / promises.length) * 100);
      };

      // Wait for all promises to complete with timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Loading timeout after 30 seconds')), 30000);
      });

      await Promise.race([
        Promise.all(promises.map(p => p.then(updateProgress).catch((err: any) => {
          console.error('Promise failed:', err);
          updateProgress(); // Still count as completed even if failed
          return null;
        }))),
        timeoutPromise
      ]);

      setDataLoaded(true);
    } catch (error) {
      console.error('❌ Failed to load data:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to load meeting data');
    } finally {
      setLoading(false);
    }
  };

  const loadMeetingData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMeeting(data.data);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load meeting data:', error);
    }
  };

  const loadChamaMembers = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(API_ENDPOINTS.CHAMA_MEMBERS(chamaId!), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setChamaMembers(data.data);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load chama members:', error);
    }
  };

  const loadExistingAttendance = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/attendance`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setAttendanceRecords(data.data);
          // Convert to attendance map
          const attendanceMap: {[userId: string]: boolean} = {};
          data.data.forEach((record: AttendanceRecord) => {
            attendanceMap[record.userId] = record.isPresent;
          });
          setAttendance(attendanceMap);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load existing attendance:', error);
      // Handle specific database schema error
      if (error instanceof Error && error.message.includes('converting NULL to string is unsupported')) {
        console.log('🔧 BACKEND SCHEMA ISSUE DETECTED - Disabling future attendance list loads.');
        setHasBackendSchemaIssue(true);
      }
    }
  };

  const loadMeetingDocuments = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) throw new Error('No auth token for documents');

      const response = await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/documents`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load documents: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        setUploadedDocuments(data.data);
      } else {
        setUploadedDocuments([]);
      }
    } catch (error) {
      console.error('❌ Failed to load meeting documents:', error);
      throw error; // Re-throw to be caught by Promise.all
    }
  };

  const loadPreviousMeetingAndMinutes = async () => {
    try {
      if (!meeting) return;

      const authToken = localStorage.getItem('authToken');
      if (!authToken) throw new Error('No auth token for previous meeting');

      // Fetch meetings for chama and pick the most recent strictly before current
      const response = await fetch(API_ENDPOINTS.CHAMA_MEETINGS(chamaId!), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load meetings: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        const currentDate = new Date(meeting.scheduledAt || meeting.date || Date.now());
        const previous = data.data
          .filter((m: any) => new Date(m.scheduledAt || m.date || 0) < currentDate)
          .sort((a: any, b: any) => new Date(b.scheduledAt || b.date || 0).getTime() - new Date(a.scheduledAt || a.date || 0).getTime())[0];

        if (previous) {
          setPreviousMeeting(previous);

          // Try to load minutes for previous meeting
          try {
            const minutesResponse = await fetch(`${API_ENDPOINTS.MEETINGS}${previous.id}/minutes`, {
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
            });

            if (minutesResponse.ok) {
              const minutesData = await minutesResponse.json();
              if (minutesData.success && minutesData.data) {
                setPreviousMinutes(minutesData.data);
              }
            }
          } catch (minutesError) {
            console.log('No minutes found for previous meeting:', minutesError);
            setPreviousMinutes(null);
          }
        } else {
          setPreviousMeeting(null);
          setPreviousMinutes(null);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load previous meeting:', error);
      throw error; // Re-throw to be caught by Promise.all
    }
  };

  const getMemberName = (member: ChamaMember) => {
    return member.name ||
           member.user_name ||
           member.username ||
           `${member.first_name || ''} ${member.last_name || ''}`.trim() ||
           member.email?.split('@')[0] ||
           `Member ${member.id.slice(-4)}`;
  };

  const toggleAttendance = async (userId: string) => {
    try {
      const currentStatus = attendance[userId] || false;
      const newStatus = !currentStatus;

      // Update local state immediately for UI responsiveness
      setAttendance(prev => ({
        ...prev,
        [userId]: newStatus
      }));

      // Save to backend immediately
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/attendance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          attendanceType: 'physical',
          isPresent: newStatus,
        }),
      });

      if (response.ok) {
        console.log(`✅ Attendance updated for user ${userId}: ${newStatus ? 'Present' : 'Absent'}`);
      } else {
        // Revert local state if backend save failed
        setAttendance(prev => ({
          ...prev,
          [userId]: currentStatus
        }));
        alert('Failed to save attendance. Please try again.');
      }
    } catch (error) {
      console.error('Failed to update attendance:', error);
      // Revert local state on error
      const currentStatus = attendance[userId] || false;
      setAttendance(prev => ({
        ...prev,
        [userId]: currentStatus
      }));
      alert('Failed to update attendance. Please check your connection.');
    }
  };

  const saveAttendance = async () => {
    try {
      setSaving(true);
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      // Save meeting notes if any
      if (notes.trim()) {
        await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/minutes`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: notes.trim(),
            status: 'draft',
            meetingId: meetingId,
            authorRole: 'secretary', // Default role
          }),
        });
      }

      // Update meeting status to completed
      try {
        await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: 'completed',
          }),
        });
      } catch (statusError) {
        console.log('⚠️ Could not update meeting status (endpoint may not exist):', statusError);
      }

      alert('Meeting data saved successfully!');
      navigate(`/dashboard/chama/${chamaId}/meetings/${meetingId}/summary`);
    } catch (error) {
      console.error('❌ Failed to save meeting data:', error);
      alert('Failed to save meeting data. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getAttendanceStats = () => {
    const present = Object.values(attendance).filter(Boolean).length;
    const total = chamaMembers.length;
    const absent = total - present;
    return { present, absent, total };
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <UserDashboardHeader
          title="Physical Meeting Attendance"
          showBackButton={true}
          showHomeButton={false}
        />
        <div style={styles.scrollableContent}>
          <div style={styles.content}>
            <div style={styles.loadingContainer}>
              <div style={{ fontSize: '48px', marginBottom: spacing.md }}>📊</div>
              <div style={{ ...styles.loadingText, color: colors.text }}>
                Loading meeting data...
              </div>
              <div style={{ ...styles.loadingProgress, backgroundColor: colors.background }}>
                <div
                  style={{
                    ...styles.loadingProgressBar,
                    backgroundColor: colors.primary,
                    width: `${loadingProgress}%`
                  }}
                />
              </div>
              <div style={{ ...styles.loadingSubtext, color: colors.textSecondary }}>
                {Math.round(loadingProgress)}% complete
              </div>
              {connectionError && (
                <div style={{ ...styles.errorMessage, color: colors.error }}>
                  {connectionError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = getAttendanceStats();

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Physical Meeting Attendance"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Meeting Info */}
          {meeting && (
            <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <h2 style={{ ...styles.meetingTitle, color: colors.text }}>{meeting.title}</h2>
              <div style={styles.meetingMeta}>
                <div style={{ ...styles.metaItem, color: colors.textSecondary }}>
                  📅 {new Date(meeting.scheduledAt).toLocaleDateString()} at {new Date(meeting.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={{ ...styles.metaItem, color: colors.textSecondary }}>
                  📍 {meeting.location}
                </div>
                <div style={{ ...styles.metaItem, color: colors.textSecondary }}>
                  ⏱️ {meeting.duration} minutes
                </div>
              </div>
            </div>
          )}

          {/* Attendance Stats */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Attendance Summary</h3>
            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.success }}>{stats.present}</div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Present</div>
              </div>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.error }}>{stats.absent}</div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Absent</div>
              </div>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.primary }}>{stats.total}</div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Total Members</div>
              </div>
            </div>
          </div>

          {/* Previous Meeting Minutes Section */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div style={styles.prevHeader}>
              <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Previous Meeting Minutes</h3>
              {prevLoading && (
                <div style={{ fontSize: '16px', color: colors.primary }}>⟳</div>
              )}
            </div>
            {previousMeeting ? (
              <>
                <div style={{ ...styles.prevMeta, color: colors.textSecondary }}>
                  Last meeting: {new Date(previousMeeting.scheduledAt || previousMeeting.date || Date.now()).toLocaleString()}
                </div>
                {previousMinutes ? (
                  <>
                    <button
                      onClick={() => setShowPrevMinutes(!showPrevMinutes)}
                      style={{ ...styles.prevToggle, borderColor: colors.border }}
                    >
                      <span style={{ color: colors.text }}>{showPrevMinutes ? '▼' : '▶'}</span>
                      <span style={{ ...styles.prevToggleText, color: colors.text }}>View last minutes</span>
                    </button>

                    {showPrevMinutes && (
                      <div style={styles.prevContent}>
                        <div style={{ ...styles.prevStatus, color: colors.textSecondary }}>
                          Status: {(previousMinutes.status || 'draft').toUpperCase()}
                        </div>
                        <div style={{ ...styles.prevBody, color: colors.text }}>
                          {previousMinutes.content || 'No content available.'}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ ...styles.prevMeta, color: colors.textSecondary }}>No minutes found for the previous meeting.</div>
                )}
              </>
            ) : (
              <div style={{ ...styles.prevMeta, color: colors.textSecondary }}>No previous meeting found.</div>
            )}
          </div>

          {/* Attendance Section */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div style={styles.attendanceHeader}>
              <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Meeting Attendance</h3>
              <div style={{ ...styles.attendanceSubtitle, color: colors.textSecondary }}>
                Tap member names to mark them as present or absent
              </div>
            </div>

            {/* Member List for Attendance */}
            {chamaMembers.length > 0 ? (
              <div style={styles.membersList}>
                {chamaMembers.map((member) => {
                  const userData = member.user || member;
                  const userId = member.user_id || member.id;
                  const firstName = userData.first_name || member.first_name || '';
                  const lastName = userData.last_name || member.last_name || '';
                  const fullName = `${firstName} ${lastName}`.trim();
                  const displayName = fullName || userData.name || member.name || userData.email?.split('@')[0] || `Member ${userId?.slice(-4)}`;
                  const memberRole = member.role || 'Member';
                  const isPresent = attendance[userId] || false;

                  return (
                    <div
                      key={userId}
                      style={{
                        ...styles.memberItem,
                        backgroundColor: isPresent ? colors.success + '15' : colors.background,
                        borderColor: isPresent ? colors.success : colors.border,
                        borderWidth: isPresent ? 2 : 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onClick={() => toggleAttendance(userId)}
                    >
                      <div style={styles.memberInfo}>
                        <div style={{ ...styles.memberAvatar, backgroundColor: colors.primary + '20' }}>
                          <span style={{ ...styles.memberAvatarText, color: colors.primary }}>
                            {firstName?.[0]?.toUpperCase() || 'M'}{lastName?.[0]?.toUpperCase() || ''}
                          </span>
                        </div>
                        <div style={styles.memberDetails}>
                          <div style={{ ...styles.memberName, color: colors.text }}>
                            {displayName}
                          </div>
                          <div style={{ ...styles.memberRole, color: colors.textSecondary }}>
                            {memberRole}
                          </div>
                        </div>
                      </div>

                      <div style={styles.attendanceStatus}>
                        {isPresent ? (
                          <div style={{ ...styles.presentIndicator, backgroundColor: colors.success + '20' }}>
                            <span style={{ fontSize: '28px', color: colors.success }}>✓</span>
                            <div>
                              <div style={{ ...styles.presentText, color: colors.success, fontWeight: '600' }}>Present</div>
                            </div>
                          </div>
                        ) : (
                          <div style={styles.absentIndicator}>
                            <span style={{ fontSize: '28px', color: colors.textSecondary }}>○</span>
                            <div>
                              <div style={{ ...styles.absentText, color: colors.textSecondary }}>Absent</div>
                              <div style={{ ...styles.tapHint, color: colors.primary, fontSize: '10px' }}>
                                Tap to mark present
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={styles.emptyState}>
                <span style={{ fontSize: '48px' }}>👥</span>
                <div style={{ ...styles.emptyStateText, color: colors.textSecondary }}>
                  Loading chama members...
                </div>
              </div>
            )}

            {/* Attendance Summary */}
            {chamaMembers.length > 0 && (
              <div style={{ ...styles.attendanceSummary, backgroundColor: colors.background }}>
                <div style={styles.summaryItem}>
                  <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Total Members:</div>
                  <div style={{ ...styles.summaryValue, color: colors.text }}>{chamaMembers.length}</div>
                </div>
                <div style={styles.summaryItem}>
                  <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Present:</div>
                  <div style={{ ...styles.summaryValue, color: colors.success }}>
                    {Object.values(attendance).filter(Boolean).length}
                  </div>
                </div>
                <div style={styles.summaryItem}>
                  <div style={{ ...styles.summaryLabel, color: colors.textSecondary }}>Absent:</div>
                  <div style={{ ...styles.summaryValue, color: colors.error }}>
                    {chamaMembers.length - Object.values(attendance).filter(Boolean).length}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Meeting Notes</h3>
            <div style={{ ...styles.notesSubtitle, color: colors.textSecondary }}>
              Record meeting minutes, decisions, and action items
            </div>
            <textarea
              style={{
                ...styles.notesInput,
                backgroundColor: colors.background,
                color: colors.text,
                borderColor: colors.border
              }}
              placeholder="Enter meeting notes, decisions, and action items..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={8}
            />
          </div>

          {/* Documents Section */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Meeting Documents</h3>
            <div style={{ ...styles.documentsSubtitle, color: colors.textSecondary }}>
              Upload meeting documents and materials
            </div>

            <div style={{ ...styles.uploadButton, borderColor: colors.primary }}>
              <span style={{ fontSize: '24px', color: colors.primary }}>📎</span>
              <span style={{ ...styles.uploadButtonText, color: colors.primary }}>
                Upload Document
              </span>
            </div>

            {uploadedDocuments.length > 0 && (
              <div style={styles.documentsList}>
                {uploadedDocuments.map((doc) => (
                  <div key={doc.id} style={{ ...styles.documentItem, borderBottomColor: colors.border }}>
                    <div style={styles.documentInfo}>
                      <span style={{ fontSize: '20px', color: colors.primary }}>📄</span>
                      <div style={styles.documentDetails}>
                        <div style={{ ...styles.documentName, color: colors.text }}>{doc.name}</div>
                        <div style={{ ...styles.documentSize, color: colors.textSecondary }}>
                          {Math.round((doc.size || 0) / 1024)} KB
                        </div>
                      </div>
                    </div>
                    <button style={{ ...styles.removeButton, color: colors.error }}>
                      <span style={{ fontSize: '16px' }}>🗑️</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button
              onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meetingId}/summary`)}
              style={{ ...styles.secondaryButton, border: `1px solid ${colors.border}`, color: colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={saveAttendance}
              disabled={saving}
              style={{
                ...styles.primaryButton,
                backgroundColor: saving ? colors.textSecondary : colors.primary,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save & Complete Meeting'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xxxl,
  },
  loadingText: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.md,
  },
  loadingProgress: {
    width: '200px',
    height: '4px',
    borderRadius: '2px',
    overflow: 'hidden',
    marginBottom: spacing.sm,
  },
  loadingProgressBar: {
    height: '100%',
    transition: 'width 0.3s ease',
  },
  loadingSubtext: {
    fontSize: typography.fontSize.sm,
  },
  errorMessage: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.sm,
    textAlign: 'center',
  },
  prevHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  prevMeta: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
  },
  prevToggle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    borderWidth: 1,
    borderRadius: borderRadius.md,
    alignSelf: 'flex-start',
    cursor: 'pointer',
    backgroundColor: 'transparent',
  },
  prevToggleText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  prevContent: {
    marginTop: spacing.md,
    padding: spacing.md,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: borderRadius.md,
  },
  prevStatus: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
  },
  prevBody: {
    fontSize: typography.fontSize.base,
    lineHeight: 1.5,
  },
  attendanceHeader: {
    marginBottom: spacing.md,
  },
  attendanceSubtitle: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs,
  },
  membersList: {
    gap: spacing.sm,
  },
  memberItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    borderWidth: 1,
  },
  memberInfo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  memberAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  memberAvatarText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
  },
  memberDetails: {
    flex: 1,
  },
  memberName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.xs / 2,
  },
  memberRole: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  attendanceStatus: {
    display: 'flex',
    alignItems: 'center',
  },
  presentIndicator: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
    paddingTop: spacing.xs,
    paddingBottom: spacing.xs,
    borderRadius: borderRadius.sm,
    gap: spacing.xs,
  },
  presentText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
  },
  absentIndicator: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  absentText: {
    fontSize: typography.fontSize.sm,
  },
  tapHint: {
    fontSize: '10px',
    fontStyle: 'italic',
    marginTop: spacing.xs / 2,
  },
  attendanceSummary: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginTop: spacing.md,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.xs / 2,
  },
  summaryValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyStateText: {
    fontSize: typography.fontSize.base,
    textAlign: 'center',
    marginTop: spacing.md,
  },
  notesSubtitle: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.md,
  },
  notesInput: {
    width: '100%',
    minHeight: 120,
    padding: spacing.md,
    borderWidth: 1,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    lineHeight: 1.5,
    resize: 'vertical',
  },
  documentsSubtitle: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.md,
  },
  uploadButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
    cursor: 'pointer',
  },
  uploadButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginLeft: spacing.sm,
  },
  documentsList: {
    marginTop: spacing.md,
  },
  documentItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
  },
  documentInfo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  documentDetails: {
    marginLeft: spacing.md,
    flex: 1,
  },
  documentName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  documentSize: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  removeButton: {
    padding: spacing.sm,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  primaryButton: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  secondaryButton: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    backgroundColor: 'transparent',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  card: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    ...getShadowStyle('sm'),
  },
  meetingTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.md,
  },
  meetingMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  metaItem: {
    fontSize: typography.fontSize.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.md,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.md,
  },
  statItem: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.xs / 2,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
  },
};

export default PhysicalMeetingAttendance;